import { LawId, RegulationId } from '@/lib/enum';

export const useEnumValidation = () => {
  /**
   * Check if a law ID exists in the LawId enum
   * @param id - Law ID to check (can be number, null, or undefined)
   * @returns true if the law exists (not DEFAULT), false otherwise
   */
  const lawExists = (id: number | null | undefined): boolean => {
    if (!id || id === 0) return false;
    const law = LawId.fromId(id);
    return law.value !== 0; // DEFAULT has value 0
  };

  /**
   * Check if a regulation ID exists in the RegulationId enum
   * @param id - Regulation ID to check (can be number, null, or undefined)
   * @returns true if the regulation exists (not DEFAULT), false otherwise
   */
  const regulationExists = (id: number | null | undefined): boolean => {
    if (!id || id === 0) return false;
    const regulation = RegulationId.fromId(id);
    return regulation.value !== 0; // DEFAULT has value 0
  };

  /**
   * Get valid regulation IDs from an array of regulation IDs
   * @param ids - Array of regulation IDs to filter
   * @returns Array of valid regulation IDs (excluding those not in enum)
   */
  const getValidRegulationIds = (
    ids: (number | null | undefined)[]
  ): number[] => {
    return ids.filter((id): id is number => regulationExists(id)) as number[];
  };

  /**
   * Check if any of the provided regulation IDs are valid
   * @param ids - Array of regulation IDs to check
   * @returns true if at least one regulation exists, false otherwise
   */
  const hasAnyValidRegulation = (
    ids: (number | null | undefined)[]
  ): boolean => {
    return ids.some((id) => regulationExists(id));
  };

  return {
    lawExists,
    regulationExists,
    getValidRegulationIds,
    hasAnyValidRegulation,
  };
};
